/* global Rollbar */

import env from '@polleverywhere/core/lib/env'
import { bindings, platform as slidewarePlatform } from '@polleverywhere/slideware-bindings'
import URI from 'urijs'
import { platform, openSSOPopover, outOfBandLogin, googPresenterLogin, environment } from './util'
import isMobileApp from '../../util/is_mobile_app'
import nativeLoginFlow from './native_login_flow'
import { defaults } from 'underscore'

const GOOG_PRESENTER_SSO_REDIRECT = '/oauth2/session_check/complete'

/**
* @option options [String] provisioning Set to 'none' to disallow provisioning
* on backend. When provisioning is disabled, a user will be unable to register a
* new user at Poll Everywhere via Google SSO.
* @option options [String] navigate Function to change the page to a new URL
*/
export default function ssoProviders (options = {}) {
  options = _.defaults(options, {
    role: 'presenter',
    provisioning: null,
    navigate: (url) => { window.location.href = url }
  })

  return {
    google: {
      callbackFn: generateCallbackFn(options, 'google')
    },
    apple: {
      callbackFn: generateCallbackFn(options, 'apple')
    }
  }
}

function generateCallbackFn (options, ssoProvider) {
  defaults(options, {
    environment: environment(options)
  })

  return async (oauth2Redirect) => {
    try {
      oauth2Redirect.provisioning = options.provisioning
      oauth2Redirect.domain = env.get('api_url')
      const nativeLoginResponse = await nativeLoginFlow(ssoProvider)
      if (nativeLoginResponse) {
        oauth2Redirect.idToken = nativeLoginResponse.idToken
        oauth2Redirect.firstName = nativeLoginResponse.firstName
        oauth2Redirect.lastName = nativeLoginResponse.lastName
      } else {
        oauth2Redirect.idToken = null
      }

      oauth2Redirect.redirectUrl = ssoRedirectLink(options.role)
      oauth2Redirect.platform = platform(`${options.role === 'participant' ? 'mobile' : 'presenter'}_`)

      // oauth role != app role, oauth is either participant or presenter
      oauth2Redirect.role = options.role === 'participant' ? options.role : 'presenter'
      oauth2Redirect.tokenRequired = isMobileApp() || options.role === 'participant' || options.role === 'auth'
      oauth2Redirect.prompt = 'select_account'

      if (oauth2Redirect.errors.length) {
        throw new Error(oauth2Redirect.errors)
      }

      switch (options.environment) {
        case 'mobile':
          openSSOPopover(oauth2Redirect.url)
          break

        case 'embed': {
          const authUrl = await outOfBandLogin(() => window.location.reload(), oauth2Redirect)
          window.open(authUrl, '', 'resizable,scrollbars,status')
          break
        }
        case 'agave': {
          const authUrl = await outOfBandLogin(options.finishedCallback, oauth2Redirect)
          options.navigate(authUrl)
          break
        }
        case 'desktop': {
          const authUrl = await outOfBandLogin(options.finishedCallback, oauth2Redirect)
          await bindings.openBrowser({ url: authUrl })
          break
        }

        case 'chrome':
          await googPresenterLogin(options.finishedCallback, oauth2Redirect)
          break

        default:
          options.navigate(oauth2Redirect.url)
          break
      }
    } catch (e) {
      if (typeof Rollbar !== 'undefined' && Rollbar !== null) {
        Rollbar.error(new Error('SSO login error'), e)
      }
      throw e
    }
  }
}

function ssoRedirectLink (role) {
  if (slidewarePlatform() === 'google') {
    return GOOG_PRESENTER_SSO_REDIRECT
  }

  // Default to where we are now
  let uri = URI()

  if (isMobileApp()) {
    if (role === 'presenter') {
      uri = URI('pollev-presenter:///')
    } else {
      uri = URI('pollev:///')
    }
  }

  if (role === 'presenter') {
    uri = uri.path('/activities')
  }

  return uri.toString()
}
